import React, { useState, useEffect } from "react";
import PageContainer from "../components/layout/PageContainer";
import CategoryNavigation from "../components/gallery/CategoryNavigation";
import GalleryViewer from "../components/gallery/GalleryViewer";
import VideoSection from "../components/gallery/VideoSection";
import { motion, AnimatePresence } from "framer-motion";
import { 
  GALLERY_IMAGES, 
  FILTER_TAGS, 
  ELEVATOR_CATEGORIES, 
  GALLERY_STATS 
} from "../assets/data/gallery";


const Gallery = () => {
  const [activeCategory, setActiveCategory] = useState("all");
  const [isGridView, setIsGridView] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  // Responsive breakpoints
  const isMobile = windowWidth <= 768;
  const isSmallScreen = windowWidth <= 480;
  const isTablet = windowWidth > 768 && windowWidth <= 1024;

  // Find currently selected category
  const currentCategory = ELEVATOR_CATEGORIES.find(
    (cat) => cat.id === activeCategory
  );

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      // Calculate scroll progress percentage
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);

      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Get responsive styles
  const getResponsiveStyles = () => {
    return {
      heroSection: {
        height: isSmallScreen ? "100vh" : "100vh",
        padding: isSmallScreen ? "60px 15px" : isMobile ? "80px 20px" : "0",
      },
      heroContent: {
        padding: isSmallScreen ? "0 10px" : isMobile ? "0 15px" : "0 20px",
        maxWidth: isSmallScreen ? "100%" : isMobile ? "90%" : "1000px",
      },
      title: {
        fontSize: isSmallScreen ? "2.5rem" : isMobile ? "3rem" : "5rem",
        letterSpacing: isSmallScreen ? "2px" : "5px",
      },
      subtitle: {
        fontSize: isSmallScreen ? "1rem" : isMobile ? "1.2rem" : "1.4rem",
        marginBottom: isSmallScreen ? "25px" : "40px",
      },
      heroStats: {
        gap: isSmallScreen ? "10px" : isMobile ? "15px" : "40px",
        flexDirection: isSmallScreen ? "column" : "row",
      },
      statNumber: {
        fontSize: isSmallScreen ? "2rem" : "2.5rem",
      },
      statDivider: {
        width: isSmallScreen ? "80%" : "1px",
        height: isSmallScreen ? "1px" : "40px",
        margin: isSmallScreen ? "10px 0" : "0",
      },
      container: {
        padding: isSmallScreen
          ? "40px 15px"
          : isMobile
          ? "60px 20px"
          : "80px 20px",
      },
      introHeading: {
        fontSize: isSmallScreen ? "2rem" : isMobile ? "2.5rem" : "3rem",
      },
      introParagraph: {
        fontSize: isSmallScreen ? "1rem" : "1.1rem",
      },
      featureGrid: {
        gridTemplateColumns: isSmallScreen
          ? "1fr"
          : isMobile
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(220px, 1fr))",
      },
      controlsContainer: {
        flexDirection: isSmallScreen ? "column" : "row",
        alignItems: isSmallScreen ? "flex-start" : "center",
      },
      galleryHeading: {
        fontSize: isSmallScreen ? "1.8rem" : isMobile ? "2rem" : "2.5rem",
      },
      innovationGrid: {
        gridTemplateColumns: isSmallScreen
          ? "1fr"
          : isMobile
          ? "1fr"
          : isTablet
          ? "repeat(2, 1fr)"
          : "repeat(auto-fill, minmax(320px, 1fr))",
        gap: isSmallScreen ? "20px" : "30px",
      },
      ctaSection: {
        padding: isSmallScreen
          ? "40px 20px"
          : isMobile
          ? "60px 25px"
          : "80px 40px",
      },
      ctaHeading: {
        fontSize: isSmallScreen ? "1.8rem" : isMobile ? "2rem" : "2.5rem",
      },
      ctaActions: {
        flexDirection: isSmallScreen ? "column" : "row",
        width: isSmallScreen ? "100%" : "auto",
      },
      ctaButtonPrimary: {
        width: isSmallScreen ? "100%" : "auto",
      },
      ctaButtonSecondary: {
        width: isSmallScreen ? "100%" : "auto",
      },
      floatingActionButton: {
        bottom: isSmallScreen ? "20px" : "30px",
        right: isSmallScreen ? "20px" : "30px",
      },
    };
  };

  const responsiveStyles = getResponsiveStyles();

  return (
    <PageContainer>
      <div style={styles.pageWrapper}>
        {/* Interactive Progress Bar */}
        <div style={styles.progressBarContainer}>
          <div
            style={{ ...styles.progressBar, width: `${scrollProgress}%` }}
          ></div>
        </div>

        {/* Modern Hero Section */}
        <div style={{ ...styles.heroSection, ...responsiveStyles.heroSection }}>
          <div style={styles.heroOverlay}></div>
          <div style={styles.gridOverlay}></div>
          <motion.div
            style={styles.floatingShape1}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, 0],
              scale: [1, 1.05, 1],
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>
          <motion.div
            style={styles.floatingShape2}
            animate={{
              y: [0, 15, 0],
              rotate: [0, -3, 0],
              scale: [1, 1.03, 1],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          ></motion.div>

          <div
            style={{ ...styles.heroContent, ...responsiveStyles.heroContent }}
          >
            <div style={{ ...styles.titleContainer, display: "inline-block" }}>
              {!isSmallScreen && (
                <motion.div
                  style={styles.titleBracket}
                  initial={{ width: 0 }}
                  animate={{ width: isMobile ? 40 : 60 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                ></motion.div>
              )}
              <motion.h1
                style={{ ...styles.title, ...responsiveStyles.title }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
              >
                ELEVATOR SHOWCASE
              </motion.h1>
              {!isSmallScreen && (
                <motion.div
                  style={{ ...styles.titleBracket, right: "0px", left: "auto" }}
                  initial={{ width: 0 }}
                  animate={{ width: isMobile ? 40 : 60 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                ></motion.div>
              )}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.7 }}
            >
              <div style={styles.titleDivider}></div>
              <p style={{ ...styles.subtitle, ...responsiveStyles.subtitle }}>
                Explore detailed views of our elevator components, installations, and design features
              </p>
              <div
                style={{ ...styles.heroStats, ...responsiveStyles.heroStats }}
              >
                <div style={styles.statItem}>
                  <span
                    style={{
                      ...styles.statNumber,
                      ...responsiveStyles.statNumber,
                    }}
                  >
                    7
                  </span>
                  <span style={styles.statLabel}>Feature Categories</span>
                </div>
                <div
                  style={{
                    ...styles.statDivider,
                    ...responsiveStyles.statDivider,
                  }}
                ></div>
                <div style={styles.statItem}>
                  <span
                    style={{
                      ...styles.statNumber,
                      ...responsiveStyles.statNumber,
                    }}
                  >
                    {GALLERY_IMAGES.length}+
                  </span>
                  <span style={styles.statLabel}>Images</span>
                </div>
                <div
                  style={{
                    ...styles.statDivider,
                    ...responsiveStyles.statDivider,
                  }}
                ></div>
                <div style={styles.statItem}>
                  <span
                    style={{
                      ...styles.statNumber,
                      ...responsiveStyles.statNumber,
                    }}
                  >
                    18+
                  </span>
                  <span style={styles.statLabel}>Years Experience</span>
                </div>
              </div>
            </motion.div>
          </div>

          {!isSmallScreen && (
            <motion.div
              style={styles.scrollPrompt}
              animate={{ y: [0, 10, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            >
              <div style={styles.scrollLine}></div>
              <div style={styles.scrollCircle}></div>
            </motion.div>
          )}
        </div>

        {/* Main Content */}
        <div style={{ ...styles.container, ...responsiveStyles.container }}>
          {/* Introduction Section */}
          <motion.div
            style={styles.introSection}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div style={styles.sectionLabel}>Features</div>
            <div style={styles.introContent}>
              <h2
                style={{
                  ...styles.introHeading,
                  ...responsiveStyles.introHeading,
                }}
              >
                <span style={styles.accentText}>Explore</span> Every Detail
              </h2>
              <p
                style={{
                  ...styles.introParagraph,
                  ...responsiveStyles.introParagraph,
                }}
              >
                Dive into our comprehensive showcase featuring detailed views of elevator components,
                from elegant interior designs and advanced door systems to smart control panels and 
                safety features. Each image demonstrates our commitment to quality craftsmanship and 
                innovative engineering solutions.
              </p>
              <div
                style={{
                  ...styles.featureGrid,
                  ...responsiveStyles.featureGrid,
                }}
              >
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <i className="fas fa-palette"></i>
                  </div>
                  <div style={styles.featureText}>Custom Designs</div>
                </div>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <i className="fas fa-shield-alt"></i>
                  </div>
                  <div style={styles.featureText}>Safety First</div>
                </div>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <i className="fas fa-microchip"></i>
                  </div>
                  <div style={styles.featureText}>Smart Technology</div>
                </div>
                <div style={styles.featureItem}>
                  <div style={styles.featureIcon}>
                    <i className="fas fa-wrench"></i>
                  </div>
                  <div style={styles.featureText}>Quality Installation</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Gallery Controls */}
          <div
            style={{
              ...styles.controlsContainer,
              ...responsiveStyles.controlsContainer,
            }}
          >
            <div style={styles.controlsHeader}>
              <div style={styles.sectionLabel}>Browse</div>
              <h3
                style={{
                  ...styles.galleryHeading,
                  ...responsiveStyles.galleryHeading,
                }}
              >
                Filter By <span style={styles.accentText}>Feature</span>
              </h3>
            </div>

            <div style={styles.viewToggleContainer}>
              <div style={styles.viewToggleLabel}>View As:</div>
              <div style={styles.viewToggle}>
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: isGridView
                      ? "rgba(0, 98, 204, 0.12)"
                      : "rgba(0, 0, 0, 0.04)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  style={
                    isGridView
                      ? { ...styles.viewButton, ...styles.activeViewButton }
                      : styles.viewButton
                  }
                  onClick={() => setIsGridView(true)}
                >
                  <i className="fas fa-th"></i>
                </motion.button>
                <motion.button
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: !isGridView
                      ? "rgba(0, 98, 204, 0.12)"
                      : "rgba(0, 0, 0, 0.04)",
                  }}
                  whileTap={{ scale: 0.95 }}
                  style={
                    !isGridView
                      ? { ...styles.viewButton, ...styles.activeViewButton }
                      : styles.viewButton
                  }
                  onClick={() => setIsGridView(false)}
                >
                  <i className="fas fa-film"></i>
                </motion.button>
              </div>
            </div>
          </div>

          {/* Navigation and Gallery Content */}
          <AnimatePresence>
            <CategoryNavigation
              categories={ELEVATOR_CATEGORIES}
              activeCategory={activeCategory}
              setActiveCategory={setActiveCategory}
              isMobile={isMobile}
              isSmallScreen={isSmallScreen}
            />

            <GalleryViewer
              currentCategory={currentCategory}
              isGridView={isGridView}
              isMobile={isMobile}
              isSmallScreen={isSmallScreen}
            />
          </AnimatePresence>

          {/* Innovation Highlight Section */}
          <motion.div
            style={styles.innovationSection}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div style={styles.innovationContent}>
              <div style={styles.sectionLabel}>Innovation</div>
              <h2
                style={{
                  ...styles.innovationHeading,
                  ...responsiveStyles.introHeading,
                }}
              >
                Next Generation{" "}
                <span style={styles.accentText}>Components</span>
              </h2>

              <div
                style={{
                  ...styles.innovationGrid,
                  ...responsiveStyles.innovationGrid,
                }}
              >
                <div style={styles.innovationCard}>
                  <div style={styles.innovationIconWrapper}>
                    <i
                      className="fas fa-home"
                      style={styles.innovationIcon}
                    ></i>
                  </div>
                  <h3 style={styles.innovationTitle}>Premium Interiors</h3>
                  <p style={styles.innovationText}>
                    Elegant cabin designs with customizable finishes, premium materials,
                    and ambient lighting systems.
                  </p>
                </div>

                <div style={styles.innovationCard}>
                  <div style={styles.innovationIconWrapper}>
                    <i
                      className="fas fa-door-open"
                      style={styles.innovationIcon}
                    ></i>
                  </div>
                  <h3 style={styles.innovationTitle}>Smart Door Systems</h3>
                  <p style={styles.innovationText}>
                    Advanced door mechanisms with smooth operation, sensor technology,
                    and customizable opening sequences.
                  </p>
                </div>

                <div style={styles.innovationCard}>
                  <div style={styles.innovationIconWrapper}>
                    <i
                      className="fas fa-sliders-h"
                      style={styles.innovationIcon}
                    ></i>
                  </div>
                  <h3 style={styles.innovationTitle}>Intelligent Controls</h3>
                  <p style={styles.innovationText}>
                    Touchscreen interfaces, destination dispatch systems, and IoT-enabled
                    monitoring for optimal performance.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
          
          {/* <VideoSection
            category={currentCategory.name}
            isMobile={isMobile}
            isSmallScreen={isSmallScreen}
          /> */}

          {/* CTA Section */}
          <motion.div
            style={{ ...styles.ctaSection, ...responsiveStyles.ctaSection }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div style={styles.ctaLight}></div>
            <div style={styles.ctaContent}>
              <h3
                style={{ ...styles.ctaHeading, ...responsiveStyles.ctaHeading }}
              >
                Ready to see these features in your building?
              </h3>
              <p style={styles.ctaText}>
                Our elevator specialists can help you choose the perfect combination of 
                features and design elements for your specific requirements.
              </p>
              <div
                style={{ ...styles.ctaActions, ...responsiveStyles.ctaActions }}
              >
                <motion.button
                  style={{
                    ...styles.ctaButtonPrimary,
                    ...responsiveStyles.ctaButtonPrimary,
                  }}
                  whileHover={{
                    scale: isSmallScreen ? 1 : 1.05,
                    boxShadow: "0 10px 25px rgba(0, 98, 204, 0.3)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span style={styles.buttonText}>Schedule Visit</span>
                  <span style={styles.buttonIcon}>
                    <i className="fas fa-calendar-alt"></i>
                  </span>
                </motion.button>
                <motion.button
                  style={{
                    ...styles.ctaButtonSecondary,
                    ...responsiveStyles.ctaButtonSecondary,
                  }}
                  whileHover={{
                    scale: isSmallScreen ? 1 : 1.05,
                    backgroundColor: "rgba(0, 98, 204, 0.05)",
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  View Specifications
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>

        
      </div>
    </PageContainer>
  );
};

const styles = {
  pageWrapper: {
    position: "relative",
    overflow: "hidden",
    background: "#ffffff",
    color: "#333333",
  },
  progressBarContainer: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "3px",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    zIndex: 1000,
  },
  progressBar: {
    height: "100%",
    background: "linear-gradient(90deg, #0062cc, #2196f3)",
    transition: "width 0.1s ease",
  },
  heroSection: {
    height: "100vh",
    width: "100%",
    position: "relative",
    backgroundImage: "linear-gradient(180deg, #040812 0%, #0c1423 100%)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  heroOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage:
      "radial-gradient(circle at 30% 40%, rgba(0, 229, 255, 0.1) 0%, rgba(0, 0, 0, 0) 70%)",
    zIndex: 1,
  },
  gridOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(rgba(0, 229, 255, 0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0, 229, 255, 0.1) 1px, transparent 1px)
    `,
    backgroundSize: "40px 40px",
    opacity: 0.3,
    zIndex: 1,
  },
  floatingShape1: {
    position: "absolute",
    width: "300px",
    height: "300px",
    borderRadius: "40% 60% 70% 30% / 40% 50% 60% 50%",
    background:
      "linear-gradient(45deg, rgba(0, 229, 255, 0.1) 0%, rgba(0, 229, 255, 0) 70%)",
    top: "15%",
    right: "10%",
    zIndex: 1,
  },
  floatingShape2: {
    position: "absolute",
    width: "250px",
    height: "250px",
    borderRadius: "60% 40% 30% 70% / 60% 30% 70% 40%",
    background:
      "linear-gradient(45deg, rgba(0, 229, 255, 0.05) 0%, rgba(0, 229, 255, 0) 70%)",
    bottom: "20%",
    left: "10%",
    zIndex: 1,
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    padding: "0 20px",
    maxWidth: "1000px",
  },
  titleContainer: {
    position: "relative",
    display: "inline-block",
    margin: "0 auto",
  },
  titleBracket: {
    position: "absolute",
    top: "50%",
    left: 140,
    transform: "translate(-120%, -50%)",
    width: 60,
    height: 2,
    backgroundColor: "#00e5ff",
    "&::before": {
      content: '""',
      position: "absolute",
      right: 0,
      height: 20,
      width: 2,
      backgroundColor: "#00e5ff",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      left: 0,
      height: 20,
      width: 2,
      backgroundColor: "#00e5ff",
    },
  },
  title: {
    fontSize: "5rem",
    fontWeight: 800,
    letterSpacing: "5px",
    color: "white",
    marginBottom: "10px",
    fontFamily: "'Inter', sans-serif",
    textShadow: "0 0 30px rgba(0, 229, 255, 0.5)",
  },
  titleDivider: {
    height: "3px",
    width: "80px",
    background: "linear-gradient(90deg, rgba(0,0,0,0), #00e5ff, rgba(0,0,0,0))",
    margin: "20px auto 25px",
    borderRadius: "2px",
  },
  subtitle: {
    fontSize: "1.4rem",
    fontWeight: 300,
    color: "rgba(255, 255, 255, 0.8)",
    maxWidth: "700px",
    margin: "0 auto 40px",
    lineHeight: 1.4,
    fontFamily: "'Poppins', sans-serif",
  },
  heroStats: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  statItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  statNumber: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#0062cc",
    lineHeight: 1,
    fontFamily: "'Montserrat', sans-serif",
  },
  statLabel: {
    fontSize: "0.9rem",
    color: "rgba(255, 255, 255, 0.6)",
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginTop: "5px",
  },
  statDivider: {
    width: "1px",
    height: "40px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  scrollPrompt: {
    position: "absolute",
    bottom: "40px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 2,
  },
  scrollLine: {
    width: "1px",
    height: "60px",
    backgroundColor: "rgba(0, 98, 204, 0.2)",
    margin: "0 auto",
    position: "relative",
  },
  scrollCircle: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#0062cc",
    margin: "0 auto",
    marginTop: "-3px",
    boxShadow: "0 0 10px rgba(0, 98, 204, 0.3)",
  },
  container: {
    padding: "80px 20px",
    maxWidth: "1400px",
    margin: "0 auto",
    position: "relative",
    zIndex: 5,
    backgroundColor: "#ffffff",
  },
  sectionLabel: {
    fontSize: "0.85rem",
    textTransform: "uppercase",
    letterSpacing: "3px",
    color: "#0062cc",
    marginBottom: "15px",
    fontWeight: 500,
  },
  introSection: {
    marginBottom: "100px",
    position: "relative",
  },
  introContent: {
    position: "relative",
    padding: "30px 0",
    borderRadius: "10px",
  },
  introHeading: {
    fontSize: "3rem",
    fontWeight: 700,
    color: "#0d1b2a",
    marginBottom: "30px",
    fontFamily: "'Montserrat', sans-serif",
  },
  accentText: {
    color: "#0062cc",
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "2px",
      backgroundColor: "#0062cc",
    },
  },
  introParagraph: {
    fontSize: "1.1rem",
    lineHeight: 1.7,
    color: "#555555",
    maxWidth: "900px",
    marginBottom: "40px",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gap: "25px",
    marginTop: "50px",
  },
  featureItem: {
    backgroundColor: "#f8faff",
    borderRadius: "10px",
    padding: "25px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    transition: "all 0.3s ease",
    border: "1px solid rgba(33, 150, 243, 0.1)",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.05)",
    "&:hover": {
      transform: "translateY(-5px)",
      backgroundColor: "white",
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)",
    },
  },
  featureIcon: {
    fontSize: "2rem",
    color: "#0062cc",
    marginBottom: "15px",
  },
  featureText: {
    fontSize: "1.1rem",
    fontWeight: 500,
    color: "#333333",
  },
  controlsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "40px",
    flexWrap: "wrap",
    gap: "20px",
  },
  controlsHeader: {
    display: "flex",
    flexDirection: "column",
  },
  galleryHeading: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#0d1b2a",
    margin: 0,
    fontFamily: "'Montserrat', sans-serif",
  },
  viewToggleContainer: {
    display: "flex",
    alignItems: "center",
    gap: "15px",
  },
  viewToggleLabel: {
    fontSize: "0.9rem",
    color: "#666666",
  },
  viewToggle: {
    display: "flex",
    gap: "8px",
    padding: "5px",
    backgroundColor: "#f8faff",
    borderRadius: "10px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  },
  viewButton: {
    background: "transparent",
    border: "none",
    borderRadius: "8px",
    padding: "10px",
    cursor: "pointer",
    fontSize: "1rem",
    transition: "all 0.3s ease",
    color: "#666666",
    width: "40px",
    height: "40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  activeViewButton: {
    backgroundColor: "#ffffff",
    color: "#0062cc",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  },
  innovationSection: {
    margin: "80px 0",
    padding: "60px 0",
    position: "relative",
    borderTop: "1px solid rgba(0, 0, 0, 0.05)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
    backgroundColor: "#f8faff",
  },
  innovationContent: {
    maxWidth: "1200px",
    margin: "0 auto",
  },
  innovationHeading: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#0d1b2a",
    marginBottom: "50px",
    fontFamily: "'Montserrat', sans-serif",
  },
  innovationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: "30px",
    marginTop: "50px",
  },
  innovationCard: {
    backgroundColor: "#ffffff",
    borderRadius: "15px",
    padding: "40px 25px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    transition: "all 0.3s ease",
    border: "1px solid rgba(33, 150, 243, 0.1)",
    boxShadow: "0 5px 20px rgba(0, 0, 0, 0.05)",
    "&:hover": {
      transform: "translateY(-5px)",
      boxShadow: "0 15px 30px rgba(0, 0, 0, 0.1)",
    },
  },
  innovationIconWrapper: {
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    backgroundColor: "#f0f7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
    border: "2px solid rgba(33, 150, 243, 0.2)",
    boxShadow: "0 5px 15px rgba(33, 150, 243, 0.15)",
  },
  innovationIcon: {
    fontSize: "1.8rem",
    color: "#0062cc",
  },
  innovationTitle: {
    fontSize: "1.3rem",
    color: "#0d1b2a",
    marginBottom: "15px",
    fontWeight: 600,
  },
  innovationText: {
    fontSize: "1rem",
    color: "#555555",
    lineHeight: 1.6,
  },
  authorInfo: {
    textAlign: "left",
  },
  authorName: {
    fontWeight: 600,
    fontSize: "1.1rem",
    color: "#0d1b2a",
    display: "block",
    marginBottom: "3px",
  },
  authorTitle: {
    fontSize: "0.9rem",
    color: "#666666",
  },
  ctaSection: {
    position: "relative",
    padding: "80px 40px",
    marginTop: "80px",
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: "#f8faff",
    boxShadow: "0 10px 40px rgba(0, 0, 0, 0.05)",
    border: "1px solid rgba(33, 150, 243, 0.1)",
  },
  ctaLight: {
    position: "absolute",
    width: "300px",
    height: "300px",
    borderRadius: "50%",
    backgroundImage:
      "radial-gradient(circle, rgba(33, 150, 243, 0.08) 0%, rgba(255, 255, 255, 0) 70%)",
    top: "-100px",
    right: "-100px",
    zIndex: 0,
  },
  ctaContent: {
    position: "relative",
    textAlign: "center",
    maxWidth: "800px",
    margin: "0 auto",
    zIndex: 2,
  },
  ctaHeading: {
    fontSize: "2.5rem",
    fontWeight: 700,
    color: "#0d1b2a",
    marginBottom: "20px",
    fontFamily: "'Montserrat', sans-serif",
  },
  ctaText: {
    fontSize: "1.2rem",
    color: "#555555",
    marginBottom: "40px",
    maxWidth: "600px",
    margin: "0 auto 40px",
    lineHeight: 1.6,
  },
  ctaActions: {
    display: "flex",
    gap: "20px",
    justifyContent: "center",
    flexWrap: "wrap",
  },
  ctaButtonPrimary: {
    backgroundColor: "#0062cc",
    color: "white",
    border: "none",
    padding: "0 30px",
    height: "56px",
    fontSize: "1rem",
    fontWeight: 600,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 5px 20px rgba(0, 98, 204, 0.2)",
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  buttonText: {
    fontWeight: 600,
  },
  buttonIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  ctaButtonSecondary: {
    backgroundColor: "white",
    color: "#0062cc",
    border: "1px solid rgba(0, 98, 204, 0.2)",
    padding: "0 30px",
    height: "56px",
    fontSize: "1rem",
    fontWeight: 500,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 3px 10px rgba(0, 0, 0, 0.05)",
  },
  floatingActionButton: {
    position: "fixed",
    bottom: "30px",
    right: "30px",
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "#0062cc",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
    cursor: "pointer",
    transition: "all 0.3s ease",
    zIndex: 100,
  },
};

export default Gallery;
